import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Search, UserPlus, Check } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Concept-2 assign-people dialog — used for both collections and
 *  resources (kind + itemId + endpoint decide where the assignee list
 *  is written). Anyone can assign per Feb 2026 spec.
 *
 *  Props:
 *    kind         "collection" | "resource"
 *    itemId       collection / resource id
 *    itemName     for the header
 *    initial      current assignee user_ids
 *    onClose      close handler
 *    onSaved      called with the fresh assignee id list after save
 */
const Concept2AssignDialog = ({ kind, itemId, itemName, initial = [], onClose, onSaved }) => {
    const [users, setUsers] = useState([]);
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(new Set(initial));
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/users");
                setUsers((r.data || []).filter((u) => u.active !== false));
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Failed to load people");
            } finally { setLoading(false); }
        })();
    }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return users;
        return users.filter((u) => `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(q));
    }, [users, query]);

    const toggle = (uid) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(uid)) next.delete(uid); else next.add(uid);
            return next;
        });
    };

    const save = async () => {
        setBusy(true);
        try {
            const path = kind === "collection"
                ? `/learning/collections/${itemId}/assign`
                : `/learning/resources/${itemId}/assign`;
            const r = await api.post(path, { user_ids: Array.from(selected) });
            const fresh = r.data?.assigned_to || Array.from(selected);
            toast.success(`Assigned to ${fresh.length} ${fresh.length === 1 ? "person" : "people"}`);
            onSaved?.(fresh);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Assign failed");
        } finally { setBusy(false); }
    };

    /* Enter-to-save: press Enter anywhere in the dialog to commit the
     * selection. Escape closes it (matches native form UX). */
    const onKey = (e) => {
        if (e.key === "Enter" && !busy) {
            e.preventDefault();
            save();
        } else if (e.key === "Escape") {
            e.preventDefault();
            onClose?.();
        }
    };

    return createPortal(
        <div className="lh-c2-modal-backdrop" onClick={onClose} data-testid="lh-c2-assign-backdrop">
            <div
                className="lh-c2-newcoll"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={onKey}
                data-testid="lh-c2-assign-modal"
                style={{ maxWidth: 480 }}
            >
                <div className="lh-c2-newcoll-head">
                    <h3>Assign {kind === "collection" ? "collection" : "resource"}</h3>
                    <button type="button" className="lh-c2-modal-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
                </div>
                <div className="lh-c2-newcoll-body">
                    <p style={{ fontSize: 12.5, color: "var(--ink-soft)", marginTop: -4 }}>
                        Share <b style={{ color: "var(--navy)" }}>{itemName}</b> with people. Press <kbd className="lh-c2-kbd">Enter</kbd> to save.
                    </p>
                    <div className="lh-c2-assign-search">
                        <Search size={14} />
                        <input
                            autoFocus
                            placeholder="Search by name or email…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            data-testid="lh-c2-assign-search"
                        />
                        <span className="lh-c2-assign-count">{selected.size} selected</span>
                    </div>
                    <div className="lh-c2-assign-list">
                        {loading ? (
                            <p style={{ color: "var(--ink-soft)", fontSize: 12.5 }}>Loading people…</p>
                        ) : filtered.length === 0 ? (
                            <p style={{ color: "var(--ink-soft)", fontSize: 12.5 }}>No matches.</p>
                        ) : (
                            filtered.map((u) => {
                                const on = selected.has(u.user_id);
                                return (
                                    <button
                                        key={u.user_id}
                                        type="button"
                                        className={`lh-c2-assign-row ${on ? "on" : ""}`}
                                        onClick={() => toggle(u.user_id)}
                                        data-testid={`lh-c2-assign-user-${u.user_id}`}
                                    >
                                        <div className="lh-c2-assign-avatar">{(u.name || u.email || "?").slice(0, 1).toUpperCase()}</div>
                                        <div className="lh-c2-assign-body">
                                            <div className="lh-c2-assign-name">{u.name || "—"}</div>
                                            <div className="lh-c2-assign-sub">{u.email}{u.department_name ? ` · ${u.department_name}` : ""}</div>
                                        </div>
                                        {on && <Check size={14} style={{ color: "var(--teal-deep)" }} />}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
                <div className="lh-c2-newcoll-footer">
                    <button type="button" className="lh-c2-btn" onClick={onClose} data-testid="lh-c2-assign-cancel">Cancel</button>
                    <button type="button" className="lh-c2-btn lh-c2-btn-solid" onClick={save} disabled={busy} data-testid="lh-c2-assign-save">
                        <UserPlus size={13} /> {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default Concept2AssignDialog;
